import { createFileRoute, Link } from "@tanstack/react-router";
import { motion } from "motion/react";
import {
  FlaskConical,
  LineChart,
  ShieldAlert,
  Sparkles,
  ArrowRight,
  TrendingDown,
  Check,
  Stethoscope,
  Scale,
  Languages,
  MessageCircleQuestion,
  PhoneCall,
} from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { breadcrumbSchema, localizedHead, medicalBusinessSchema, validateLangSearch } from "@/lib/seo";
import { RelatedLinks } from "@/components/related-links";
import { ConsultCta } from "@/components/consult-cta";
import { TestimonialsSlider } from "@/components/testimonials-slider";

import { HeroImage } from "@/components/hero-image";
import {
  heroMoleculeImage,
  thailandClinicImage,
  lifestyleImage,
  conciergeImage,
  retaVsOzempicDeImage,
  retaVsOzempicEnImage,
} from "@/lib/responsive-images";
import beforeAfterEn from "@/assets/retatrutide-before-after-4-weeks.jpg.asset.json";
import beforeAfterDe from "@/assets/retatrutide-vorher-nachher-4-wochen.jpg.asset.json";

export const Route = createFileRoute("/")({
  validateSearch: validateLangSearch,
  loaderDeps: ({ search }) => ({ lang: search.lang }),
  loader: ({ deps }) => ({ lang: deps.lang }),
  head: ({ loaderData }) => ({
    ...localizedHead("/", loaderData?.lang),
    links: [
      {
        rel: "preload",
        as: "image",
        type: "image/avif",
        href: heroMoleculeImage.src,
        imageSrcSet: heroMoleculeImage.srcSetAvif,
        imageSizes: heroMoleculeImage.sizes,
      },
      {
        rel: "preload",
        as: "image",
        type: "image/webp",
        href: heroMoleculeImage.src,
        imageSrcSet: heroMoleculeImage.srcSetWebp,
        imageSizes: heroMoleculeImage.sizes,
      },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(breadcrumbSchema("/", loaderData?.lang)),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(medicalBusinessSchema(loaderData?.lang)),
      },
    ],
  }),
  component: Index,
});

function Index() {
  const { t, lang } = useI18n();
  const de = lang === "de";

  const stepIcons = [PhoneCall, Stethoscope, FlaskConical, LineChart];
  const whyIcons = [Scale, Stethoscope, Languages];

  const tiles = [
    {
      to: "/wissenschaft" as const,
      icon: Sparkles,
      eyebrow: t.home.tiles.evoEyebrow,
      title: t.home.tiles.evoTitle,
      body: t.home.tiles.evoBody,
      accent: "indigo",
    },
    {
      to: "/studien" as const,
      icon: LineChart,
      eyebrow: t.home.tiles.triumphEyebrow,
      title: t.home.tiles.triumphTitle,
      body: t.home.tiles.triumphBody,
      accent: "emerald",
    },
    {
      to: "/sicherheit" as const,
      icon: ShieldAlert,
      eyebrow: t.home.tiles.safeEyebrow,
      title: t.home.tiles.safeTitle,
      body: t.home.tiles.safeBody,
      accent: "warning",
    },
  ];

  return (
    <div className="gradient-hero">
      {/* 1 · HERO */}
      <section className="mx-auto max-w-6xl px-4 pt-12 pb-10 md:pt-24 md:pb-16">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl"
        >
          <span className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs font-medium text-muted-foreground">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald" /> {t.home.badge}
          </span>
          <h1 className="mt-5 text-balance font-display text-4xl leading-[1.05] text-foreground md:text-6xl lg:text-7xl">
            {t.home.h1a} <em className="not-italic text-indigo">{t.home.h1em}</em> {t.home.h1b}
          </h1>
          <p className="mt-6 max-w-2xl text-balance text-base leading-relaxed text-muted-foreground md:text-lg">
            {t.home.lead}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <ConsultCta />
            <a
              href="#ablauf"
              className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-3 text-sm font-semibold text-foreground transition-colors hover:border-indigo/50"
            >
              {t.home.ctaHow}
              <ArrowRight className="h-4 w-4" />
            </a>
          </div>
          <ul className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-xs text-muted-foreground md:text-sm">
            {[t.home.trustRow.a, t.home.trustRow.b, t.home.trustRow.c].map((item) => (
              <li key={item} className="inline-flex items-center gap-2">
                <Check className="h-4 w-4 text-emerald" aria-hidden="true" />
                {item}
              </li>
            ))}
          </ul>
        </motion.div>

        <figure className="mt-10 overflow-hidden rounded-2xl border border-border bg-card shadow-xl">
          <img
            src={de ? beforeAfterDe.url : beforeAfterEn.url}
            alt={
              de
                ? "Retatrutid Vorher-Nachher: 4 Wochen Behandlung, −9,0 kg Gewichtsverlust (83,0 kg auf 74,0 kg)"
                : "Retatrutide before and after: 4 weeks of treatment, −9.0 kg (−19.8 lbs) weight loss (83.0 kg to 74.0 kg)"
            }
            width={de ? 1400 : 1400}
            height={de ? 700 : 788}
            loading="lazy"
            decoding="async"
            className="h-auto w-full"
          />
          <figcaption className="px-4 py-3 text-center text-xs text-muted-foreground">
            {de
              ? "Beispielhafter Verlauf einer 4-wöchigen Retatrutid-Behandlung. Individuelle Ergebnisse variieren."
              : "Example progression over a 4-week retatrutide treatment. Individual results vary."}
          </figcaption>
        </figure>

        <HeroImage
          {...heroMoleculeImage}
          alt={
            de
              ? "Abstrakte Darstellung eines Triple-Agonist-GLP-1-Peptidmoleküls"
              : "Abstract rendering of a triple-agonist GLP-1 peptide molecule"
          }
          eager
        />
      </section>

      {/* 2 · INTRODUCTION */}
      <section className="mx-auto max-w-6xl px-4 py-16 md:py-20">
        <div className="max-w-3xl">
          <span className="text-[10px] font-semibold uppercase tracking-widest text-indigo">
            {t.home.intro.eyebrow}
          </span>
          <h2 className="mt-3 font-display text-3xl text-foreground md:text-4xl">
            {t.home.intro.title}
          </h2>
          <div className="mt-6 space-y-4 text-base leading-relaxed text-muted-foreground">
            {t.home.intro.paragraphs.map((p) => (
              <p key={p.slice(0, 40)}>{p}</p>
            ))}
          </div>
        </div>
      </section>

      {/* 3 · HOW IT WORKS */}
      <section id="ablauf" className="mx-auto max-w-6xl px-4 py-16 md:py-20">
        <div className="max-w-2xl">
          <span className="text-[10px] font-semibold uppercase tracking-widest text-indigo">
            {t.home.how.eyebrow}
          </span>
          <h2 className="mt-3 font-display text-3xl text-foreground md:text-4xl">
            {t.home.how.title}
          </h2>
          <p className="mt-3 text-muted-foreground">{t.home.how.lead}</p>
        </div>
        <div className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          {t.home.how.steps.map((step, i) => {
            const Icon = stepIcons[i] ?? PhoneCall;
            return (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="card-elevated flex h-full flex-col gap-4 p-6"
              >
                <span className="grid h-10 w-10 place-items-center rounded-xl bg-indigo/15 text-indigo">
                  <Icon className="h-5 w-5" />
                </span>
                <h3 className="text-lg font-semibold text-foreground">{step.title}</h3>
                <p className="text-sm text-muted-foreground">{step.body}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* 4 · WHAT'S INCLUDED */}
      <section className="mx-auto max-w-6xl px-4 py-16 md:py-20">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:items-center">
          <div>
            <span className="text-[10px] font-semibold uppercase tracking-widest text-indigo">
              {t.home.included.eyebrow}
            </span>
            <h2 className="mt-3 font-display text-3xl text-foreground md:text-4xl">
              {t.home.included.title}
            </h2>
            <p className="mt-3 text-muted-foreground">{t.home.included.lead}</p>
            <p className="mt-6 rounded-xl border border-border bg-card p-4 text-sm text-muted-foreground">
              {t.home.included.priceNote}
            </p>
            <div className="mt-6">
              <ConsultCta />
            </div>
          </div>
          <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {t.home.included.items.map((item) => (
              <li
                key={item}
                className="card-elevated flex items-start gap-3 p-4 text-sm text-foreground"
              >
                <span className="mt-0.5 grid h-6 w-6 flex-none place-items-center rounded-full bg-emerald/15 text-emerald">
                  <Check className="h-3.5 w-3.5" />
                </span>
                {item}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* 5 · RESULTS */}
      <section className="mx-auto max-w-6xl px-4 py-16 md:py-20">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:items-center">
          <div>
            <span className="text-[10px] font-semibold uppercase tracking-widest text-emerald">
              {t.home.results.eyebrow}
            </span>
            <h2 className="mt-3 font-display text-3xl text-foreground md:text-4xl">
              {t.home.results.title}
            </h2>
            <p className="mt-3 text-muted-foreground">{t.home.results.lead}</p>

            <div className="mt-6 rounded-2xl border border-emerald/30 bg-gradient-to-br from-emerald/10 via-background to-indigo/10 p-6">
              <p className="flex items-baseline gap-2">
                <TrendingDown className="h-6 w-6 text-emerald" />
                <span className="font-display text-6xl leading-none text-foreground md:text-7xl">
                  −24<span className="text-3xl text-muted-foreground">%</span>
                </span>
              </p>
              <p className="mt-3 text-sm text-muted-foreground">{t.home.results.metricLabel}</p>

              <div className="mt-6 space-y-3 text-sm">
                {[
                  { g: "Gen 1", n: "Semaglutid", v: "~15 %", w: 62, hl: false },
                  { g: "Gen 2", n: "Tirzepatid", v: "~22 %", w: 92, hl: false },
                  { g: "Gen 3", n: "Retatrutid", v: "~24 %", w: 100, hl: true },
                ].map((r) => (
                  <div key={r.n} className="flex items-center gap-3">
                    <span className="w-14 text-xs font-mono text-muted-foreground">{r.g}</span>
                    <span className="flex-1 font-medium text-foreground">{r.n}</span>
                    <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
                      <div
                        className={`h-full rounded-full ${r.hl ? "bg-emerald" : "bg-indigo/60"}`}
                        style={{ width: `${r.w}%` }}
                      />
                    </div>
                    <span className="w-14 text-right text-xs font-semibold text-foreground">
                      {r.v}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <p className="mt-4 text-xs text-muted-foreground">{t.home.results.disclaimer}</p>

            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/studien"
                className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-3 text-sm font-semibold text-foreground transition-colors hover:border-indigo/50"
              >
                {t.home.results.ctaStudies}
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/rechner"
                className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-3 text-sm font-semibold text-foreground transition-colors hover:border-indigo/50"
              >
                {t.home.results.ctaCalc}
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>

          <HeroImage
            {...lifestyleImage}
            className="mt-0"
            alt={
              de
                ? "Aktives Paar nach erfolgreicher Retatrutid-Behandlung – Lifestyle nach Gewichtsverlust"
                : "Active couple after successful retatrutide treatment – lifestyle after weight loss"
            }
          />
        </div>
      </section>

      {/* 5b · RETA vs OZEMPIC TEASER */}
      <section className="mx-auto max-w-6xl px-4 py-16 md:py-20">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:items-center">
          <div>
            <span className="text-[10px] font-semibold uppercase tracking-widest text-indigo">
              {de ? "Reta vs Ozempic" : "Reta vs Ozempic"}
            </span>
            <h2 className="mt-3 font-display text-3xl text-foreground md:text-4xl">
              {de
                ? "Reta zeigt stärkere Ergebnisse im Studienvergleich"
                : "Reta shows stronger results in study comparison"}
            </h2>
            <p className="mt-3 text-muted-foreground">
              {de
                ? "Retatrutide (Triple-Agonist auf GLP-1, GIP und Glucagon) erreicht in TRIUMPH-1 bis zu 28,3 % durchschnittlichen Gewichtsverlust – gegenüber ~14,9 % unter Semaglutid (Ozempic/Wegovy) in STEP 1."
                : "Retatrutide (a triple agonist on GLP-1, GIP and glucagon) reaches up to 28.3% average weight loss in TRIUMPH-1 — vs ~14.9% with Semaglutide (Ozempic/Wegovy) in STEP 1."}
            </p>
            <p className="mt-3 text-xs text-muted-foreground">
              {de
                ? "Kein direkter Head-to-Head-Vergleich. Werte aus unterschiedlichen Studien mit unterschiedlicher Dauer, Dosierung und Population. Retatrutide ist derzeit ein Prüfpräparat."
                : "Not a direct head-to-head comparison. Values from different trials with different duration, dosing and populations. Retatrutide is currently an investigational drug."}
            </p>
            <div className="mt-6">
              <Link
                to="/reta-vs-ozempic"
                className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-3 text-sm font-semibold text-foreground transition-colors hover:border-indigo/50"
              >
                {de ? "Zum Vergleich Reta vs Ozempic" : "Read the Reta vs Ozempic comparison"}
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
          <HeroImage
            {...(de ? retaVsOzempicDeImage : retaVsOzempicEnImage)}
            className="mt-0 mx-auto max-w-md"
            alt={
              de
                ? "Reta vs Ozempic – Studienvergleich: Retatrutide 28,3 % vs Semaglutid 14,9 % durchschnittlicher Gewichtsverlust"
                : "Reta vs Ozempic – study comparison: Retatrutide 28.3% vs Semaglutide 14.9% average weight loss"
            }
          />
        </div>
      </section>

      {/* 6 · WHY US */}
      <section className="mx-auto max-w-6xl px-4 py-16 md:py-20">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:items-center">
          <HeroImage
            {...thailandClinicImage}
            className="mt-0"
            alt={
              de
                ? "Moderne Partner-Klinik in Bangkok"
                : "Modern partner clinic in Bangkok"
            }
          />
          <div>
            <span className="text-[10px] font-semibold uppercase tracking-widest text-indigo">
              {t.home.why.eyebrow}
            </span>
            <h2 className="mt-3 font-display text-3xl text-foreground md:text-4xl">
              {t.home.why.title}
            </h2>
            <div className="mt-6 space-y-4">
              {t.home.why.cards.map((card, i) => {
                const Icon = whyIcons[i] ?? Scale;
                return (
                  <div
                    key={card.title}
                    className="card-elevated flex items-start gap-4 p-5"
                  >
                    <span className="grid h-10 w-10 flex-none place-items-center rounded-xl bg-indigo/15 text-indigo">
                      <Icon className="h-5 w-5" />
                    </span>
                    <div>
                      <h3 className="font-semibold text-foreground">{card.title}</h3>
                      <p className="mt-1 text-sm text-muted-foreground">{card.body}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* 7 · SCIENCE & SAFETY (Trust-Layer) */}
      <section className="mx-auto max-w-6xl px-4 py-16 md:py-20">
        <div className="max-w-2xl">
          <span className="text-[10px] font-semibold uppercase tracking-widest text-indigo">
            {t.home.scienceIntro.eyebrow}
          </span>
          <h2 className="mt-3 font-display text-3xl text-foreground md:text-4xl">
            {t.home.scienceIntro.title}
          </h2>
          <p className="mt-3 text-muted-foreground">{t.home.scienceIntro.lead}</p>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-3">
          {tiles.map((tile, i) => {
            const Icon = tile.icon;
            return (
              <motion.div
                key={tile.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
              >
                <Link
                  to={tile.to}
                  className="card-elevated card-hover group relative flex h-full flex-col justify-between overflow-hidden p-6"
                >
                  <div className="flex items-start justify-between">
                    <span
                      className={`grid h-10 w-10 place-items-center rounded-xl ${
                        tile.accent === "emerald"
                          ? "bg-emerald/15 text-emerald"
                          : tile.accent === "warning"
                            ? "bg-warning/15 text-warning"
                            : "bg-indigo/15 text-indigo"
                      }`}
                    >
                      <Icon className="h-5 w-5" />
                    </span>
                    <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                      {tile.eyebrow}
                    </span>
                  </div>
                  <div className="mt-6">
                    <h3 className="text-lg font-semibold text-foreground md:text-xl">
                      {tile.title}
                    </h3>
                    <p className="mt-2 text-sm text-muted-foreground">{tile.body}</p>
                  </div>
                  <ArrowRight className="absolute bottom-5 right-5 h-4 w-4 text-muted-foreground opacity-0 transition-all group-hover:translate-x-1 group-hover:opacity-100" />
                </Link>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* 8 · FAQ TEASER */}
      <section className="mx-auto max-w-6xl px-4 py-16 md:py-20">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div className="max-w-2xl">
            <span className="text-[10px] font-semibold uppercase tracking-widest text-indigo">
              {t.home.faqTeaser.eyebrow}
            </span>
            <h2 className="mt-3 font-display text-3xl text-foreground md:text-4xl">
              {t.home.faqTeaser.title}
            </h2>
          </div>
          <Link
            to="/faq"
            className="inline-flex items-center gap-2 text-sm font-semibold text-indigo hover:underline"
          >
            <MessageCircleQuestion className="h-4 w-4" />
            {t.home.faqTeaser.cta}
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-2">
          {t.home.faqTeaser.items.map((item) => (
            <div key={item.q} className="card-elevated p-6">
              <h3 className="font-semibold text-foreground">{item.q}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{item.a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 9 · FINAL CTA */}
      <section className="mx-auto max-w-6xl px-4 pb-20">
        <div className="overflow-hidden rounded-3xl border border-indigo/30 bg-gradient-to-br from-indigo/15 via-background to-emerald/10 p-8 md:p-12">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-[2fr_1fr] lg:items-center">
            <div>
              <h2 className="font-display text-3xl text-foreground md:text-4xl">
                {t.home.finalCta.title}
              </h2>
              <p className="mt-3 max-w-2xl text-muted-foreground">{t.home.finalCta.lead}</p>
              <p className="mt-4 text-xs font-semibold uppercase tracking-widest text-emerald">
                {t.home.finalCta.reassure}
              </p>
              <div className="mt-6">
                <ConsultCta />
              </div>
            </div>
            <HeroImage
              {...conciergeImage}
              className="mt-0"
              alt={
                de
                  ? "Deutschsprachige persönliche Betreuung – Retatrutid-Behandlung in Thailand"
                  : "Personal English/German-speaking care – retatrutide treatment in Thailand"
              }
            />
          </div>
        </div>

        <TestimonialsSlider />

        <RelatedLinks only={["/thailand", "/vergleich", "/zulassung", "/faq"]} />

      </section>
    </div>
  );
}
